import { queryClient } from '@/queries/index';
import { quoteQueries } from '@/queries/quotes/quotes.query';
import { employeeQueries } from '@/queries/employee';
import { customerQueries } from '@/queries/customer';
import { CustomerMT } from '@/api/apis/customer';
import { Employee } from '@/api/apis/resources';
import { Case, Quote, QuoteStatus, Review, ReviewerType, ReviewType } from '@/api/apis/case';
import { Uuid } from '@/api/types';
import { caseQueryKeys } from '@/queries/case/index';

const getQuoteReviewerInfo = (
  quote: Quote,
  reviews: Review[],
): {
  type: 'Employee' | 'Customer';
  reviewerUserId: string;
} | null => {
  if (quote.status === QuoteStatus.Recalled) {
    const reviewerId = quote.recallComment?.userId;

    if (!reviewerId) {
      return null;
    }

    return {
      type: 'Employee',
      reviewerUserId: reviewerId,
    };
  }
  const reviewer = reviews.findLast((approval) => approval.type === ReviewType.TXCenterQuote);

  if (reviewer?.reviewerType !== ReviewerType.Employee && reviewer?.reviewerType !== ReviewerType.Customer) {
    return null;
  }

  return {
    type: reviewer.reviewerType,
    reviewerUserId: reviewer.reviewerUserId,
  };
};

export const getQuoteReviewerData = (caseId: Uuid): Promise<CustomerMT | Employee | null> => {
  const caseData = queryClient.getQueryData(caseQueryKeys.getItem(caseId)) as Case;
  if (
    !caseData.quote ||
    ![QuoteStatus.Approved, QuoteStatus.Rejected, QuoteStatus.Recalled].includes(caseData.quote.status)
  ) {
    return Promise.resolve(null);
  }

  const quoteReviewerInfo = getQuoteReviewerInfo(caseData.quote, caseData.reviews);
  if (!quoteReviewerInfo) {
    throw new Error(`Quote reviewer should exist before getting quote reviewer for caseId: ${caseId}`);
  }

  if (quoteReviewerInfo.type === 'Employee') {
    return queryClient.ensureQueryData(employeeQueries.itemByUserId(quoteReviewerInfo.reviewerUserId));
  }

  return queryClient.ensureQueryData(customerQueries.getByUserIdSimple(quoteReviewerInfo.reviewerUserId));
};

export const seedQuoteReviewerData = async (caseId: Uuid, routeQuoteId: Uuid) => {
  const quoteReviewerData = await getQuoteReviewerData(caseId);

  queryClient.setQueryData(quoteQueries.getQuoteReviewerData(caseId, routeQuoteId).queryKey, quoteReviewerData);
};

export const invalidateQuoteReviewer = (caseId: Uuid, routeQuoteId?: Uuid) => {
  if (routeQuoteId) {
    queryClient.invalidateQueries({ queryKey: quoteQueries.getQuoteReviewerData(caseId, routeQuoteId).queryKey });
  }
};
